import { CrudPage } from '../../components/CrudPage';
import { type MaterialDto, MaterialControllerService } from '../../api';
import MaterialFormDialog from '../forms/MaterialFormDialog';
import { useTranslation } from 'react-i18next';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';

// Type with required id for the CrudPage
type MaterialWithId = MaterialDto & { id: string };

const ProductsPage = () => {
  const { t } = useTranslation();

  const formatBaseUnit = (data: any) => {
    const value = data.baseUnit;
    if (!value) return '';
    
    // Map backend enum values to translation keys
    const unitMap: { [key: string]: string } = {
      'COUNT': 'piece',
      'GRAM': 'gram',
      'KILOGRAM': 'kilogram',
      'MILLIGRAM': 'milligram',
      'TON': 'ton',
      'LITER': 'liter',
      'MILLILITER': 'milliliter',
      'CUBIC_METER': 'cubicMeter',
      'METER': 'meter',
      'CENTIMETER': 'centimeter',
      'MILLIMETER': 'millimeter',
      'KILOMETER': 'kilometer',
      'SQUARE_METER': 'squareMeter',
      'SQUARE_CENTIMETER': 'squareCentimeter',
      'SQUARE_MILLIMETER': 'squareMillimeter'
    };
    
    const translationKey = unitMap[value] || value.toLowerCase();
    return t(`form.baseUnit.${translationKey}`);
  };

  const formatScrapRate = (data: any) => {
    const value = data.scrapRate;
    if (value === null || value === undefined) return '';
    return `${(value * 100).toFixed(2)}%`;
  };

  // Renderer for min dough quantity
  const formatMinDoughQuantity = (data: any) => {
    // Syncfusion Grid template passes data in props.data, but CrudPage may wrap it
    // Try both: direct access and props.data
    const material = data?.data || data?.rowData || data;
    const value = material?.minDoughQuantityInKg;
    
    return value != null ? `${value} kg` : '-';
  };

  // Renderer for max dough quantity
  const formatMaxDoughQuantity = (data: any) => {
    // Syncfusion Grid template passes data in props.data, but CrudPage may wrap it
    // Try both: direct access and props.data
    const material = data?.data || data?.rowData || data;
    const value = material?.maxDoughQuantityInKg;
    return value != null ? `${value} kg` : '-';
  };

  // Checkbox template for global field
  const GlobalCheckboxTemplate = (data: any) => {
    return (
      <div className="flex justify-center">
        <CheckBoxComponent
          checked={data.global}
          disabled={true}
          cssClass="e-success"
        />
      </div>
    );
  };

  const AliasCheckboxTemplate = (data: any) => {
    return (
      <div className="flex justify-center">
        <CheckBoxComponent
          checked={data.isAlias}
          disabled={true}
          cssClass="e-success"
        />
      </div>
    );
  };

  const gridColumns = [
    { field: 'name', headerKey: 'grid.name', width: 150 },
    { field: 'description', headerKey: 'grid.description', width: 200 },
    { field: 'articleNumber', headerKey: 'material.articleNumber', width: 120 },
    { field: 'baseUnit', headerKey: 'form.baseUnit', width: 120, render: formatBaseUnit },
    { field: 'initialWeighInGrams', headerKey: 'material.initialWeighIn', width: 120, textAlign: 'Right' },
    { field: 'scrapRate', headerKey: 'material.scrapRate', width: 100, textAlign: 'Right', render: formatScrapRate },
    { field: 'minDoughQuantityInKg', headerKey: 'material.minDoughQuantity', width: 120, textAlign: 'Right', render: formatMinDoughQuantity },
    { field: 'maxDoughQuantityInKg', headerKey: 'material.maxDoughQuantity', width: 120, textAlign: 'Right', render: formatMaxDoughQuantity },
    { field: 'isAlias', headerKey: 'product.isAlias', width: 100, render: AliasCheckboxTemplate },
    { field: 'aliasForProductName', headerKey: 'product.aliasForProduct', width: 180 },
    { field: 'global', headerKey: 'form.global', width: 100, render: GlobalCheckboxTemplate }
  ];

  return (
    <CrudPage<MaterialWithId>
      titleKey="routes.products"
      gridMeta={gridColumns}
      fetchData={async () => {
        const materials = await MaterialControllerService.findAll();
        const products = materials.filter(material => material.materialType === 'PRODUCT');
        const productNameByUuid = new Map(materials.map(material => [material.uuid, material.name]));
        return products.map(material => ({
          ...material,
          id: material.uuid || '',
          aliasForProductName: material.aliasForProductUuid
            ? (productNameByUuid.get(material.aliasForProductUuid) || '')
            : ''
        }));
      }}
      saveData={async (data: MaterialWithId) => {
        // Ensure materialType is set to PRODUCT
        const productData = { ...data, materialType: 'PRODUCT' as const };

        if (productData.id) {
          await MaterialControllerService.update(productData.id, productData);
        } else {
          await MaterialControllerService.create(productData);
        }
      }}
      deleteData={MaterialControllerService.delete}
      FormComponent={(props) => <MaterialFormDialog {...props} materialType="PRODUCT" />}
      entityName="material"
    />
  );
};

export default ProductsPage;
